import { useRef, useEffect, useCallback, useState } from 'react';
import { User, Shield, Star, Eye, Zap, Users, ZoomIn, ZoomOut, Maximize2, GitBranch } from 'lucide-react';
import type { Contact, ContactUseCaseScore } from '../../../types';
import {
  buildOrgTree,
  getHighlightFromPriority,
  getEffectivePriority,
  HIGHLIGHT_COLORS,
  HIGHLIGHT_LABELS,
  BUYING_ROLE_LABELS,
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  type OrgNode,
  type ContactHighlight,
} from '../../../lib/orgChartUtils';
import type { OutboundPriority } from '../../../types';

interface Props {
  contacts: Contact[];
  ucScoreMap: Map<string, ContactUseCaseScore>;
  onSelectContact: (contact: Contact) => void;
  selectedContactId?: string;
  onAssignManager: (contact: Contact) => void;
  focusContactId?: string | null;
  onFocusHandled?: () => void;
}

function HighlightLegend() {
  return (
    <div className="flex flex-wrap gap-3 px-4 py-2.5 bg-app-canvas rounded-lg border border-app-line">
      {(Object.entries(HIGHLIGHT_COLORS) as [ContactHighlight, typeof HIGHLIGHT_COLORS[ContactHighlight]][]).map(([key, colors]) => (
        <div key={key} className="flex items-center gap-1.5">
          <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
          <span className="text-[11px] text-app-muted">{HIGHLIGHT_LABELS[key]}</span>
        </div>
      ))}
    </div>
  );
}

function BuyingRoleIcon({ role }: { role: string }) {
  switch (role) {
    case 'economic_buyer': return <Shield className="w-3 h-3 text-app-critical" />;
    case 'technical_buyer': return <Zap className="w-3 h-3 text-app-focus" />;
    case 'champion': return <Star className="w-3 h-3 text-app-warning" />;
    case 'influencer': return <Users className="w-3 h-3 text-app-warning" />;
    case 'validator': return <Eye className="w-3 h-3 text-app-focus" />;
    default: return null;
  }
}

function NodeCard({ contact, ucScore, isSelected, onSelect, onAssignManager }: {
  contact: Contact;
  ucScore?: ContactUseCaseScore;
  isSelected: boolean;
  onSelect: (c: Contact) => void;
  onAssignManager: (c: Contact) => void;
}) {
  const effectivePriority = ucScore ? getEffectivePriority(ucScore) : null;
  const highlight = effectivePriority
    ? getHighlightFromPriority(effectivePriority)
    : 'low';
  const colors = HIGHLIGHT_COLORS[highlight];
  const priorityLabel = effectivePriority
    ? PRIORITY_LABELS[effectivePriority] || ''
    : '';
  const isOverridden = ucScore?.priority_override != null;

  return (
    <div className="relative group" data-contact-id={contact.id}>
      <button
        onClick={() => onSelect(contact)}
        className={`w-44 text-left p-2.5 rounded-lg border-2 transition hover:shadow-none bg-app-surface ${
          isSelected
            ? 'border-forest-500 bg-forest-50 ring-2 ring-forest-200 shadow-none'
            : `${colors.border} hover:shadow-none`
        }`}
      >
        <div className="flex items-center gap-2 mb-1">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
            <User className={`w-3.5 h-3.5 ${colors.text}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <p className="text-xs font-semibold text-app-ink truncate leading-tight">
                {contact.first_name} {contact.last_name}
              </p>
            </div>
            <p className="text-[10px] text-app-muted truncate leading-tight">{contact.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap mt-1">
          {contact.department && (
            <span className="text-[9px] px-1 py-0.5 bg-app-canvas rounded text-app-muted truncate max-w-[70px]">
              {contact.department}
            </span>
          )}
          <BuyingRoleIcon role={contact.buying_committee_role} />
          {priorityLabel && highlight !== 'low' && (
            <span className={`text-[9px] font-semibold px-1 py-0.5 rounded ${colors.bg} ${colors.text}`}>
              {priorityLabel}
            </span>
          )}
          {isOverridden && (
            <span className="text-[8px] font-medium px-1 py-0.5 rounded bg-app-warning/10 text-app-warning">OVR</span>
          )}
        </div>
        {ucScore && (
          <div className="flex items-center justify-between mt-1">
            <span className="text-[9px] text-app-muted">{ucScore.use_case_relevance_score}% match</span>
            {contact.buying_committee_role && (
              <span className="text-[9px] text-app-muted truncate max-w-[80px]">
                {BUYING_ROLE_LABELS[contact.buying_committee_role]}
              </span>
            )}
          </div>
        )}
      </button>
      <button
        onClick={e => {
          e.stopPropagation();
          onAssignManager(contact);
        }}
        className="absolute -right-1 -top-1 p-1 bg-app-surface border border-app-line rounded-md text-app-line-strong hover:text-forest-600 hover:border-forest-300 hover:bg-forest-50 transition-colors opacity-0 group-hover:opacity-100 shadow-none z-10"
        title="Assign manager"
      >
        <GitBranch className="w-3 h-3" />
      </button>
    </div>
  );
}

function VerticalTreeNode({ node, onSelect, selectedId, scoreMap, onAssignManager }: {
  node: OrgNode;
  onSelect: (c: Contact) => void;
  selectedId?: string;
  scoreMap: Map<string, ContactUseCaseScore>;
  onAssignManager: (c: Contact) => void;
}) {
  const contact = node.contact;
  const ucScore = scoreMap.get(contact.id);
  const hasChildren = node.children.length > 0;

  return (
    <li className="relative flex flex-col items-center pt-5">
      <NodeCard
        contact={contact}
        ucScore={ucScore}
        isSelected={selectedId === contact.id}
        onSelect={onSelect}
        onAssignManager={onAssignManager}
      />
      {hasChildren && (
        <ul className="flex flex-row flex-nowrap gap-0 pt-5 relative">
          {node.children.map(child => (
            <VerticalTreeNode
              key={child.contact.id}
              node={child}
              onSelect={onSelect}
              selectedId={selectedId}
              scoreMap={scoreMap}
              onAssignManager={onAssignManager}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 1.5;
const ZOOM_STEP = 0.15;
const DEFAULT_ZOOM = 0.95;

function findTopPersonaId(ucScoreMap: Map<string, ContactUseCaseScore>): string | null {
  let bestId: string | null = null;
  let bestOrder = Infinity;
  let bestRelevance = -1;

  for (const [contactId, score] of ucScoreMap) {
    const order = PRIORITY_ORDER[getEffectivePriority(score)] ?? 99;
    if (
      order < bestOrder ||
      (order === bestOrder && score.use_case_relevance_score > bestRelevance)
    ) {
      bestOrder = order;
      bestRelevance = score.use_case_relevance_score;
      bestId = contactId;
    }
  }
  return bestId;
}

const treeStyles = `
.org-tree ul { position: relative; }
.org-tree li { position: relative; padding: 0 8px; }
.org-tree li::before,
.org-tree li::after {
  content: '';
  position: absolute;
  top: 0;
  border-top: 2px solid rgb(var(--color-app-line-strong));
}
.org-tree li::before {
  left: 0;
  right: 50%;
  height: 20px;
}
.org-tree li::after {
  right: 0;
  left: 50%;
  height: 20px;
}
.org-tree li:first-child::before { display: none; }
.org-tree li:last-child::after { display: none; }
.org-tree li:first-child::after { border-radius: 8px 0 0 0; }
.org-tree li:last-child::before { border-radius: 0 8px 0 0; }
.org-tree li:only-child::before,
.org-tree li:only-child::after {
  display: none;
}
.org-tree > li::before,
.org-tree > li::after {
  display: none;
}
.org-tree ul::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  width: 0;
  height: 20px;
  border-left: 2px solid rgb(var(--color-app-line-strong));
}
.org-tree > ul::before { display: none; }
`;

export default function OrgChartTree({ contacts, ucScoreMap, onSelectContact, selectedContactId, onAssignManager, focusContactId, onFocusHandled }: Props) {
  const tree = buildOrgTree(contacts);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const hasCenteredRef = useRef(false);

  const scrollToContact = useCallback((contactId: string, currentZoom: number) => {
    if (!containerRef.current || !contentRef.current) return;
    const node = contentRef.current.querySelector(`[data-contact-id="${contactId}"]`);
    if (!node) return;
    const container = containerRef.current;
    const nodeEl = node as HTMLElement;
    const nodeRect = nodeEl.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();

    const nodeOffsetLeft = (nodeRect.left - contentRect.left) / currentZoom;
    const nodeOffsetTop = (nodeRect.top - contentRect.top) / currentZoom;
    const nodeWidth = nodeRect.width / currentZoom;
    const nodeHeight = nodeRect.height / currentZoom;

    const scrollLeft = (nodeOffsetLeft + nodeWidth / 2) * currentZoom - container.clientWidth / 2;
    const scrollTop = (nodeOffsetTop + nodeHeight / 2) * currentZoom - container.clientHeight / 2;

    container.scrollTo({
      left: Math.max(0, scrollLeft),
      top: Math.max(0, scrollTop),
      behavior: 'smooth',
    });
  }, []);

  const computeFitZoom = useCallback(() => {
    if (!containerRef.current || !contentRef.current) return DEFAULT_ZOOM;
    const containerW = containerRef.current.clientWidth;
    const containerH = containerRef.current.clientHeight;
    const contentW = contentRef.current.scrollWidth;
    const contentH = contentRef.current.scrollHeight;
    if (contentW === 0 || contentH === 0) return DEFAULT_ZOOM;
    const fitW = containerW / contentW;
    const fitH = containerH / contentH;
    return Math.max(MIN_ZOOM, Math.min(1, Math.min(fitW, fitH)));
  }, []);

  useEffect(() => {
    if (hasCenteredRef.current) return;
    const timer = setTimeout(() => {
      setZoom(DEFAULT_ZOOM);
      const topId = findTopPersonaId(ucScoreMap);
      if (topId) {
        scrollToContact(topId, DEFAULT_ZOOM);
      }
      hasCenteredRef.current = true;
    }, 200);
    return () => clearTimeout(timer);
  }, [contacts.length, ucScoreMap, scrollToContact]);

  useEffect(() => {
    if (!focusContactId) return;
    const timer = setTimeout(() => {
      scrollToContact(focusContactId, zoom);
      onFocusHandled?.();
    }, 100);
    return () => clearTimeout(timer);
  }, [focusContactId, zoom, scrollToContact, onFocusHandled]);

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
        <p className="text-sm text-app-muted">No contacts to display in org chart.</p>
      </div>
    );
  }

  const deptGroups = new Map<string, Contact[]>();
  contacts.forEach(c => {
    const dept = c.department || 'General';
    if (!deptGroups.has(dept)) deptGroups.set(dept, []);
    deptGroups.get(dept)!.push(c);
  });

  const avgConfidence = Math.round(
    contacts.reduce((sum, c) => sum + c.org_placement_confidence, 0) / contacts.length
  );

  const scoredContacts = contacts.filter(c => {
    const s = ucScoreMap.get(c.id);
    return s && s.persona_match_score > 0;
  });
  const avgPersonaMatch = scoredContacts.length > 0
    ? Math.round(
        scoredContacts.reduce((sum, c) => sum + (ucScoreMap.get(c.id)?.persona_match_score || 0), 0) /
        scoredContacts.length
      )
    : 0;

  const targetCount = contacts.filter(c => {
    const s = ucScoreMap.get(c.id);
    if (!s) return false;
    const ep = getEffectivePriority(s);
    return ep === 'target_persona' || ep === 'decision_maker';
  }).length;

  return (
    <div className="space-y-4">
      <style>{treeStyles}</style>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="p-3 bg-app-surface rounded-lg border border-app-line text-center">
          <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Org Accuracy</p>
          <p className="text-lg font-bold text-app-ink">{avgConfidence}%</p>
        </div>
        <div className="p-3 bg-app-surface rounded-lg border border-app-line text-center">
          <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Persona Match</p>
          <p className="text-lg font-bold text-app-ink">{avgPersonaMatch}%</p>
        </div>
        <div className="p-3 bg-app-surface rounded-lg border border-app-line text-center">
          <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Key Targets</p>
          <p className="text-lg font-bold text-app-ink">{targetCount}</p>
        </div>
        <div className="p-3 bg-app-surface rounded-lg border border-app-line text-center">
          <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Departments</p>
          <p className="text-lg font-bold text-app-ink">{deptGroups.size}</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <HighlightLegend />
        <div className="flex items-center gap-1 ml-3">
          <button
            onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
            className="p-1.5 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-[10px] font-medium text-app-muted w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
            className="p-1.5 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={() => setZoom(computeFitZoom())}
            className="p-1.5 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors"
            title="Fit to view"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="overflow-auto max-h-[calc(100vh-380px)] rounded-lg border border-app-line bg-app-canvas"
      >
        <div
          ref={contentRef}
          className="p-8 inline-block min-w-full transition-transform duration-200"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
        >
          <ul className="org-tree flex justify-center" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {tree.map(node => (
              <VerticalTreeNode
                key={node.contact.id}
                node={node}
                onSelect={onSelectContact}
                selectedId={selectedContactId}
                scoreMap={ucScoreMap}
                onAssignManager={onAssignManager}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
